import { useState } from "react";
import { Check, Pencil, HelpCircle, Camera } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Card, LevelBadge } from "@/components/Common";
import PhotoUploader from "@/components/PhotoUploader";
import { PhotoGrid } from "@/components/analysis/PhotoStep";
import SecondOpinion from "@/components/analysis/SecondOpinion";

export const PhotoRequests = ({ analysis, requests, reload, onSkip, skipped = [] }) => (
  <div className="space-y-3" data-testid="photo-requests">
    {requests.map((r) => (
      <Card key={r.id} accent testid={`photo-request-${r.id}`}>
        <div className="flex items-start gap-3">
          <Camera size={18} className="mt-0.5 text-[#B39257]" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-[#0D2517]">Serve una foto: {r.label}</p>
            {r.instructions && <p className="mt-1 text-sm text-[#2D3A30]">{r.instructions}</p>}
            {r.why && <p className="mt-1 text-xs text-[#5b6b60]">Perché: {r.why}</p>}
          </div>
        </div>
        {r.status === "done" ? (
          <p className="mt-3 text-xs font-semibold text-[#15803D]">Foto aggiunta ✓</p>
        ) : skipped.includes(r.id) ? (
          <p className="mt-3 text-xs text-[#8a958d]">Non disponibile</p>
        ) : (
          <>
            <PhotoUploader analysisId={analysis.id} kind={r.kind} requestId={r.id} onUploaded={reload} testid={`req-uploader-${r.id}`} />
            {onSkip && (
              <button onClick={() => onSkip(r.id)} className="mt-2 w-full text-center text-xs text-[#5b6b60] underline" data-testid={`photo-request-skip-${r.id}`}>
                Non posso fotografarlo
              </button>
            )}
          </>
        )}
      </Card>
    ))}
  </div>
);

export default function IdentificationStep({ analysis, reload }) {
  const ident = analysis.identification;
  const ai = ident.ai || {};
  const level = ident.confidence.level;
  const [edit, setEdit] = useState(false);
  const [names, setNames] = useState({ common_name: "", botanical_name: "" });
  const [busy, setBusy] = useState(false);
  const unknownLow = analysis.species_confirmation === "non_so" && (level === "bassa" || ident.confidence.discordant);

  const confirm = async (choice, extra = {}) => {
    setBusy(true);
    try {
      await api.post(`/analyses/${analysis.id}/confirm-species`, { choice, ...extra });
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const reidentify = async () => {
    await api.post(`/analyses/${analysis.id}/identify`).catch((e) => toast.error(errMsg(e)));
    reload();
  };

  return (
    <div className="space-y-5 px-5 py-6" data-testid="identification-step">
      <div>
        <p className="eyebrow">Passo 2 · Riconoscimento</p>
        {ai.botanical_name && level !== "bassa" ? (
          <h1 className="font-serif text-3xl font-semibold leading-tight text-[#0D2517]" data-testid="identification-result">
            La pianta sembra essere: {ai.common_name} <span className="botanical text-[#B39257]">({ai.botanical_name})</span>. È corretto?
          </h1>
        ) : (
          <h1 className="font-serif text-3xl font-semibold leading-tight text-[#0D2517]" data-testid="identification-uncertain">
            Non riesco a identificare la pianta con sufficiente attendibilità
          </h1>
        )}
        <div className="mt-3"><LevelBadge level={level} label="Attendibilità identificazione" testid="identification-confidence" /></div>
        {ident.confidence.reasons?.length > 0 && (
          <p className="mt-2 text-xs text-[#5b6b60]" data-testid="identification-reasons">Motivi: {ident.confidence.reasons.join("; ")}.</p>
        )}
      </div>

      <PhotoGrid photos={analysis.photos || []} />

      {ai.visible_characters?.length > 0 && (
        <Card testid="identification-characters">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#5b6b60]">Caratteri osservati</p>
          <ul className="list-disc space-y-1 pl-5 text-sm">{ai.visible_characters.map((c) => <li key={c}>{c}</li>)}</ul>
        </Card>
      )}

      {ai.alternatives?.length > 0 && (
        <Card testid="identification-alternatives">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#5b6b60]">Alternative plausibili</p>
          <div className="space-y-2">
            {ai.alternatives.map((alt, i) => (
              <button key={i} disabled={busy} onClick={() => confirm("modifica", { common_name: alt.common_name, botanical_name: alt.botanical_name })}
                className="w-full rounded-xl border border-[#ddc69c]/70 p-3 text-left hover:border-[#0D2517]" data-testid={`alternative-${i}`}>
                <span className="text-sm font-semibold">{alt.common_name}</span> <span className="botanical text-[#B39257]">{alt.botanical_name}</span>
                {alt.why && <span className="block text-xs text-[#5b6b60]">{alt.why}</span>}
              </button>
            ))}
          </div>
        </Card>
      )}

      <SecondOpinion ident={ident} busy={busy} onPick={() => confirm("plantnet")} />

      {ident.photo_requests?.length > 0 && (
        <>
          <PhotoRequests analysis={analysis} requests={ident.photo_requests} reload={reload} />
          <button onClick={reidentify} className="btn-secondary w-full" data-testid="reidentify-button">Ripeti il riconoscimento con le nuove foto</button>
        </>
      )}

      {unknownLow && <p className="rounded-xl bg-[#B91C1C]/5 p-3 text-sm text-[#7f1d1d]" data-testid="need-more-photos-msg">Per procedere servono altre fotografie (foglie, fiori, frutti o pianta intera) oppure indica tu la specie.</p>}

      {!edit ? (
        <div className="grid gap-2">
          {ai.botanical_name && level !== "bassa" && (
            <button disabled={busy} onClick={() => confirm("si")} className="btn-primary w-full" data-testid="confirm-species-yes"><Check size={18} /> Sì</button>
          )}
          <button disabled={busy} onClick={() => setEdit(true)} className="btn-secondary w-full bg-white" data-testid="confirm-species-edit"><Pencil size={16} /> No, modifica</button>
          <button disabled={busy} onClick={() => confirm("non_so")} className="btn-secondary w-full bg-white" data-testid="confirm-species-unknown"><HelpCircle size={16} /> Non lo so</button>
        </div>
      ) : (
        <Card testid="species-edit-form">
          <input className="field mb-2" placeholder="Nome comune (es. Limone)" value={names.common_name} onChange={(e) => setNames({ ...names, common_name: e.target.value })} data-testid="species-common-input" />
          <input className="field mb-3" placeholder="Nome botanico, se lo conosci" value={names.botanical_name} onChange={(e) => setNames({ ...names, botanical_name: e.target.value })} data-testid="species-botanical-input" />
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setEdit(false)} className="btn-secondary" data-testid="species-edit-cancel">Annulla</button>
            <button disabled={busy || !(names.common_name || names.botanical_name)} onClick={() => confirm("modifica", names)} className="btn-primary" data-testid="species-edit-save">Conferma</button>
          </div>
        </Card>
      )}
    </div>
  );
}
